
import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  deleteIndustrialCostDirectCostBlock,
  getIndustrialCostDirectCostBlocks,
  getIndustrialCostDirectCostCategories,
  getIndustrialCostProducts,
  upsertIndustrialCostDirectCostBlock,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  DirectCostBlockSummary,
  Product,
  SourceGranularity,
  UpsertDirectCostBlockPayload,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
const UNCLASSIFIED_CATEGORY_CODE = '__unclassified__';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string | { message?: string } } };
      message?: string;
    };

    const detail = maybeResponse.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string') {
      return detail.message;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}

function resolveDirectCostGranularity(
  sourceGranularity: SourceGranularity | null | undefined,
  directCostDate: string | null | undefined,
  periodMonth: number | null | undefined,
): SourceGranularity {
  if (sourceGranularity === 'day' || sourceGranularity === 'month' || sourceGranularity === 'year') {
    return sourceGranularity;
  }

  if (directCostDate) {
    return 'day';
  }

  if (periodMonth !== null && periodMonth !== undefined) {
    return 'month';
  }

  return 'year';
}

function buildDirectCostTemporalContext(form: {
  source_granularity: SourceGranularity;
  direct_cost_date: string;
  period_year: string;
  period_month: string;
}): {
  direct_cost_date: string | null;
  period_year: number;
  period_month: number | null;
  source_granularity: SourceGranularity;
} | null {
  if (form.source_granularity === 'day') {
    if (!form.direct_cost_date.trim()) {
      return null;
    }

    const parts = form.direct_cost_date.split('-').map((item) => Number(item));
    if (parts.length !== 3 || parts.some((item) => Number.isNaN(item))) {
      return null;
    }

    const [year, month] = parts;

    return {
      direct_cost_date: form.direct_cost_date,
      period_year: year,
      period_month: month,
      source_granularity: 'day',
    };
  }

  if (form.source_granularity === 'month') {
    if (
      form.period_year.trim().length === 0 ||
      form.period_month.trim().length === 0 ||
      Number.isNaN(Number(form.period_year)) ||
      Number.isNaN(Number(form.period_month))
    ) {
      return null;
    }

    return {
      direct_cost_date: null,
      period_year: Number(form.period_year),
      period_month: Number(form.period_month),
      source_granularity: 'month',
    };
  }

  if (form.period_year.trim().length === 0 || Number.isNaN(Number(form.period_year))) {
    return null;
  }

  return {
    direct_cost_date: null,
    period_year: Number(form.period_year),
    period_month: null,
    source_granularity: 'year',
  };
}

type BlockFormState = {
  product_id: string;
  source_granularity: SourceGranularity;
  direct_cost_date: string;
  period_year: string;
  period_month: string;
  source_ref: string;
  notes: string;
  categoryAmounts: Record<string, string>;
};

const currentYear = new Date().getFullYear();

const initialBlockForm: BlockFormState = {
  product_id: '',
  source_granularity: 'month',
  direct_cost_date: '',
  period_year: String(currentYear),
  period_month: '',
  source_ref: '',
  notes: '',
  categoryAmounts: {},
};

export default function IndustrialCostDirectCostsView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<DirectCostBlockSummary | null>(null);
  const [blockForm, setBlockForm] = useState<BlockFormState>(initialBlockForm);

  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1);
  const [productFilter, setProductFilter] = useState<'all' | string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [categoriesQuery, blocksQuery, productsQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-direct-cost-categories'],
        queryFn: getIndustrialCostDirectCostCategories,
      },
      {
        queryKey: ['industrial-cost-direct-cost-blocks'],
        queryFn: getIndustrialCostDirectCostBlocks,
      },
      {
        queryKey: ['industrial-cost-products'],
        queryFn: getIndustrialCostProducts,
      },
    ],
  });

  const categories = categoriesQuery.data ?? [];
  const blocks = blocksQuery.data ?? [];
  const products = productsQuery.data ?? [];

  const activeCategories = useMemo(
    () =>
      [...categories]
        .filter((item) => item.is_active)
        .sort((a, b) => a.display_order - b.display_order || a.label.localeCompare(b.label)),
    [categories],
  );

  const availableYears = useMemo(
    () =>
      Array.from(new Set([currentDate.getFullYear(), ...blocks.map((block) => block.period_year)]))
        .sort((a, b) => b - a),
    [blocks, currentDate],
  );

  const productsMap = useMemo(
    () => new Map<string, Product>(products.map((item) => [item.id, item])),
    [products],
  );

  const sortedProducts = useMemo(
    () =>
      [...products].sort((a, b) =>
        `${a.code || ''} ${a.name || ''}`.localeCompare(`${b.code || ''} ${b.name || ''}`),
      ),
    [products],
  );

  const selectedProductOption = useMemo(
    () => (productFilter === 'all' ? null : sortedProducts.find((product) => product.id === productFilter) ?? null),
    [productFilter, sortedProducts],
  );

  const blockFormSelectedProduct = useMemo(
    () => sortedProducts.find((product) => product.id === blockForm.product_id) ?? null,
    [sortedProducts, blockForm.product_id],
  );

  const yearBlocks = useMemo(
    () => blocks.filter((item) => item.period_year === selectedYear),
    [blocks, selectedYear],
  );

  const monthBlocks = useMemo(() => {
    const exact = yearBlocks.filter((item) => item.period_month === selectedMonth);
    if (exact.length > 0) return exact;

    // Si no hay datos mensuales, distribuir los anuales entre 12
    const annual = yearBlocks.filter((item) => item.period_month === null);
    return annual.map((block) => {
      const distributedCategories: Record<string, number> = {};
      if (block.categories) {
        Object.entries(block.categories).forEach(([key, value]) => {
          distributedCategories[key] = Number(value || 0) / 12;
        });
      }
      return {
        ...block,
        period_month: selectedMonth,
        total_direct_cost: Number(block.total_direct_cost || 0) / 12,
        categories: distributedCategories,
      };
    });
  }, [yearBlocks, selectedMonth]);

  const filteredBlocks = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return monthBlocks.filter((item) => {
      if (productFilter !== 'all' && item.product_id !== productFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const product = productsMap.get(item.product_id);

      return [
        product?.code,
        product?.name,
        item.source_ref,
        item.notes,
      ]
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    });
  }, [monthBlocks, productFilter, productsMap, searchQuery]);

  const annualDirectTotal = useMemo(
    () => yearBlocks.reduce((sum, block) => sum + Number(block.total_direct_cost || 0), 0),
    [yearBlocks],
  );

  const monthlyDirectTotal = useMemo(
    () => monthBlocks.reduce((sum, block) => sum + Number(block.total_direct_cost || 0), 0),
    [monthBlocks],
  );

  const monthlyCategoryTotals = useMemo(() => {
    const totals = activeCategories.map((category) => ({
      code: category.code,
      label: category.label,
      amount: monthBlocks.reduce(
        (sum, block) => sum + Number(block.categories?.[category.code] || 0),
        0,
      ),
      highlight: false,
    }));

    const unclassifiedTotal = monthBlocks.reduce(
      (sum, block) => sum + Number(block.categories?.[UNCLASSIFIED_CATEGORY_CODE] || 0),
      0,
    );

    return [
      { code: '__total_period__', label: 'Total del periodo', amount: monthlyDirectTotal, highlight: false },
      { code: '__total_year__', label: 'Total anual', amount: annualDirectTotal, highlight: false },
      ...totals,
      { code: UNCLASSIFIED_CATEGORY_CODE, label: 'Sin clasificar', amount: unclassifiedTotal, highlight: true },
    ];
  }, [activeCategories, annualDirectTotal, monthBlocks, monthlyDirectTotal]);

  const resetBlockForm = () => {
    setEditingBlock(null);
    setBlockForm({
      ...initialBlockForm,
      period_year: String(selectedYear),
      period_month: String(selectedMonth),
      source_granularity: 'month',
    });
  };

  const blockTemporalContext = useMemo(
    () => buildDirectCostTemporalContext(blockForm),
    [blockForm],
  );

  const informedCategoryCount = useMemo(() => {
    const currentCodes = [...activeCategories.map((category) => category.code), UNCLASSIFIED_CATEGORY_CODE];
    return currentCodes.filter((code) => {
      const value = blockForm.categoryAmounts[code];
      return value && value.trim().length > 0;
    }).length;
  }, [activeCategories, blockForm.categoryAmounts]);

  const canSubmitBlock =
    blockForm.product_id.length > 0 &&
    blockTemporalContext !== null &&
    informedCategoryCount > 0;

  const blockMutation = useMutation({
    mutationFn: () => {
      const lines = [...activeCategories, { code: UNCLASSIFIED_CATEGORY_CODE } as const].map((category) => {
        const rawValue = blockForm.categoryAmounts[category.code];
        return {
          category_code: category.code,
          amount_total: rawValue && rawValue.trim().length > 0 ? Number(rawValue) : null,
        };
      });

      const temporalContext = buildDirectCostTemporalContext(blockForm);
      if (!temporalContext) {
        throw new Error('Debes informar un contexto temporal válido antes de guardar.');
      }

      const payload: UpsertDirectCostBlockPayload = {
        product_id: blockForm.product_id,
        direct_cost_date: temporalContext.direct_cost_date,
        period_year: temporalContext.period_year,
        period_month: temporalContext.period_month,
        source_granularity: temporalContext.source_granularity,
        source_type: 'manual',
        source_ref: blockForm.source_ref.trim() || null,
        notes: blockForm.notes.trim() || null,
        lines,
      };

      return upsertIndustrialCostDirectCostBlock(payload);
    },
    onSuccess: async (summary) => {
      const skippedCount = summary.categories_skipped_empty.length;
      const skippedText = skippedCount > 0 ? ` Categorías sin importe: ${summary.categories_skipped_empty.join(', ')}.` : '';
      enqueueSnackbar(`Bloque guardado correctamente. Categorías guardadas: ${summary.categories_saved.length}.${skippedText}`, {
        variant: skippedCount > 0 ? 'warning' : 'success',
      });
      setBlockDialogOpen(false);
      resetBlockForm();
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-direct-cost-blocks'] });
    },
    onError: (mutationError) => enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' }),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (block: DirectCostBlockSummary) =>
      deleteIndustrialCostDirectCostBlock(
        block.product_id,
        block.period_year,
        block.period_month,
        block.direct_cost_date,
        block.source_granularity,
        'manual',
      ),
    onSuccess: async () => {
      enqueueSnackbar('Capa manual eliminada correctamente.', { variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-direct-cost-blocks'] });
    },
    onError: (mutationError) => enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' }),
  });

  const isLoading = categoriesQuery.isLoading || blocksQuery.isLoading || productsQuery.isLoading;
  const isError = categoriesQuery.isError || blocksQuery.isError || productsQuery.isError;
  const error = categoriesQuery.error ?? blocksQuery.error ?? productsQuery.error;

  const handleOpenCreateBlock = () => {
    resetBlockForm();
    setBlockDialogOpen(true);
  };

  const handleOpenEditBlock = (block: DirectCostBlockSummary) => {
    const nextCategoryAmounts: Record<string, string> = {};

    [...activeCategories, { code: UNCLASSIFIED_CATEGORY_CODE } as const].forEach((category) => {
      const manualValue = block.category_source_breakdown?.[category.code]?.manual;
      const fallbackValue = block.source_type === 'manual' ? block.categories?.[category.code] : undefined;
      const value = manualValue ?? fallbackValue;
      nextCategoryAmounts[category.code] = value === undefined || value === null ? '' : String(value);
    });

    const resolvedGranularity = resolveDirectCostGranularity(
      block.source_granularity,
      block.direct_cost_date,
      block.period_month,
    );

    setEditingBlock(block);
    setBlockForm({
      product_id: block.product_id,
      source_granularity: resolvedGranularity,
      direct_cost_date: block.direct_cost_date || '',
      period_year: String(block.period_year),
      period_month: block.period_month ? String(block.period_month) : '',
      source_ref: block.source_type === 'manual' ? block.source_ref || '' : '',
      notes: block.source_type === 'manual' ? block.notes || '' : '',
      categoryAmounts: nextCategoryAmounts,
    });
    setBlockDialogOpen(true);
  };

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Directos aplicados
              </Typography>
              <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
                Visualiza el coste directo consolidado por producto y periodo. Esta pantalla muestra el valor final registrado y su reparto entre clasificaciones.
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
                Si existe coste sin clasificar, sigue visible dentro del total y aparece separado como su propia categoría.
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                select
                size="small"
                label="Año"
                value={selectedYear}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
                sx={{ minWidth: 110 }}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Mes"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
                sx={{ minWidth: 130 }}
              >
                {[
                  ['1', 'Enero'], ['2', 'Febrero'], ['3', 'Marzo'], ['4', 'Abril'], ['5', 'Mayo'], ['6', 'Junio'],
                  ['7', 'Julio'], ['8', 'Agosto'], ['9', 'Septiembre'], ['10', 'Octubre'], ['11', 'Noviembre'], ['12', 'Diciembre'],
                ].map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 320px' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Cobertura del periodo
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, mb: 2.25 }}>
                Resumen económico del mes seleccionado. Se muestra el total consolidado y su reparto por clasificaciones activas.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {monthlyCategoryTotals.map((item) => (
                  <Paper
                    key={item.code}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: item.highlight
                        ? '1px solid rgba(245,158,11,0.28)'
                        : '1px solid rgba(148,163,184,0.16)',
                      bgcolor: item.highlight ? 'rgba(120,53,15,0.16)' : 'rgba(2,6,23,0.32)',
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontSize: '1.35rem', fontWeight: 900, mt: 0.75 }}>
                      {formatCurrency(item.amount)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {item.code === '__total_period__'
                        ? `${String(selectedMonth).padStart(2, '0')}/${selectedYear}`
                        : item.code === '__total_year__'
                          ? `${selectedYear}`
                          : 'Periodo seleccionado'}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(34,211,238,0.28)',
                bgcolor: 'rgba(8,47,73,0.34)',
                px: 2,
                py: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Acciones
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, mb: 2 }}>
                Registra ajustes manuales cuando necesites completar o corregir el coste directo consolidado.
              </Typography>

              <Button variant="contained" fullWidth onClick={handleOpenCreateBlock}>
                Nuevo ajuste manual
              </Button>
            </Paper>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid rgba(148,163,184,0.14)',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) auto' },
              gap: 2,
              alignItems: 'end',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Directos aplicados por producto y periodo
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                Visualiza el coste directo consolidado por producto. La tabla muestra el total y el reparto entre clasificaciones para el periodo seleccionado.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '260px 260px' },
                gap: 1,
              }}
            >
              <TextField
                label="Buscar"
                placeholder="Producto, referencia o nota"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                size="small"
              />

              <Autocomplete
                options={sortedProducts}
                value={selectedProductOption}
                onChange={(_, value) => setProductFilter(value?.id ?? 'all')}
                getOptionLabel={(option) => [option.code, option.name].filter(Boolean).join(' · ')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producto"
                    placeholder="Todos los productos"
                    size="small"
                  />
                )}
              />
            </Box>
          </Box>

          {isError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{getErrorMessage(error)}</Alert>
            </Box>
          ) : isLoading ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Cargando costes directos...
              </Typography>
            </Box>
          ) : blocks.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin directos aplicados todavía"
                description="Todavía no hay directos aplicados para mostrar. Cuando existan bloques consolidados, aparecerán aquí agrupados por producto y periodo."
              />
            </Box>
          ) : filteredBlocks.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin resultados para el filtro actual"
                description="Ajusta la búsqueda o el filtro de producto para volver a mostrar directos aplicados del periodo."
              />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                    {activeCategories.map((category) => (
                      <TableCell key={category.code} sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                        {category.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                      Sin clasificar
                    </TableCell>
                    <TableCell sx={{ color: '#f8fafc', fontWeight: 900 }} align="right">
                      Total directo
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBlocks.map((block) => {
                    const product = productsMap.get(block.product_id);
                    const rowKey = `${block.product_id}-${block.direct_cost_date ?? 'null'}-${block.period_year}-${block.period_month ?? 'null'}-${resolveDirectCostGranularity(block.source_granularity, block.direct_cost_date, block.period_month)}`;

                    return (
                      <TableRow key={rowKey} hover>
                        <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                              {product?.name || block.product_id}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {product?.code || 'Sin código'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {activeCategories.map((category) => (
                          <TableCell key={category.code} align="right" sx={{ color: '#cbd5e1' }}>
                            {formatAmount(block.categories?.[category.code] ?? null)}
                          </TableCell>
                        ))}

                        <TableCell
                          align="right"
                          sx={{
                            color: Number(block.categories?.[UNCLASSIFIED_CATEGORY_CODE] || 0) > 0 ? '#cbd5e1' : '#94a3b8',
                            fontWeight: 600,
                          }}
                        >
                          {formatAmount(block.categories?.[UNCLASSIFIED_CATEGORY_CODE] ?? null)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            fontSize: '1rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatCurrency(block.total_direct_cost)}
                        </TableCell>

                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenEditBlock(block)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={!block.source_type_breakdown?.manual}
                            title={block.source_type_breakdown?.manual ? 'Eliminar capa manual' : 'No hay capa manual que eliminar'}
                            onClick={() => deleteBlockMutation.mutate(block)}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={blockDialogOpen}
        onClose={() => !blockMutation.isPending && setBlockDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingBlock ? 'Editar ajuste manual de directos' : 'Nuevo ajuste manual de directos'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Solo se guardarán las categorías con importe informado. Las categorías activas sin importe no bloquearán el guardado.
            </Alert>
            <Alert severity="info" variant="outlined">
              Usa este formulario para correcciones, ajustes puntuales o partidas directas que deban incorporarse al coste final del periodo.
            </Alert>

            <Autocomplete
              options={sortedProducts}
              value={blockFormSelectedProduct}
              onChange={(_, value) =>
                setBlockForm((prev) => ({
                  ...prev,
                  product_id: value?.id ?? '',
                }))
              }
              getOptionLabel={(option) => [option.code, option.name].filter(Boolean).join(' · ')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Producto"
                  placeholder="Busca un producto..."
                  fullWidth
                  size="small"
                />
              )}
            />

            <TextField
              select
              label="Granularidad"
              value={blockForm.source_granularity}
              onChange={(e) => {
                const nextGranularity = e.target.value as SourceGranularity;
                setBlockForm((prev) => ({
                  ...prev,
                  source_granularity: nextGranularity,
                  direct_cost_date: nextGranularity === 'day' ? prev.direct_cost_date : '',
                  period_month: nextGranularity === 'month' ? prev.period_month : '',
                }));
              }}
              fullWidth
              size="small"
            >
              <MenuItem value="day">Diaria</MenuItem>
              <MenuItem value="month">Mensual</MenuItem>
              <MenuItem value="year">Anual</MenuItem>
            </TextField>

            {blockForm.source_granularity === 'day' ? (
              <TextField
                label="Fecha real"
                type="date"
                value={blockForm.direct_cost_date}
                onChange={(e) => setBlockForm((prev) => ({ ...prev, direct_cost_date: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: blockForm.source_granularity === 'month' ? '1fr 1fr' : '1fr' },
                  gap: 1.5,
                }}
              >
                <TextField
                  label="Año"
                  type="number"
                  value={blockForm.period_year}
                  onChange={(e) => setBlockForm((prev) => ({ ...prev, period_year: e.target.value }))}
                  fullWidth
                  size="small"
                />
                {blockForm.source_granularity === 'month' ? (
                  <TextField
                    label="Mes"
                    type="number"
                    value={blockForm.period_month}
                    onChange={(e) => setBlockForm((prev) => ({ ...prev, period_month: e.target.value }))}
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 12 }}
                  />
                ) : null}
              </Box>
            )}

            <TextField
              label="Referencia"
              value={blockForm.source_ref}
              onChange={(e) => setBlockForm((prev) => ({ ...prev, source_ref: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              label="Notas"
              value={blockForm.notes}
              onChange={(e) => setBlockForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              {activeCategories.map((category) => (
                <TextField
                  key={category.code}
                  label={category.label}
                  type="number"
                  value={blockForm.categoryAmounts[category.code] ?? ''}
                  onChange={(e) =>
                    setBlockForm((prev) => ({
                      ...prev,
                      categoryAmounts: {
                        ...prev.categoryAmounts,
                        [category.code]: e.target.value,
                      },
                    }))
                  }
                  fullWidth
                  size="small"
                />
              ))}

              <TextField
                label="Sin clasificar"
                type="number"
                value={blockForm.categoryAmounts[UNCLASSIFIED_CATEGORY_CODE] ?? ''}
                onChange={(e) =>
                  setBlockForm((prev) => ({
                    ...prev,
                    categoryAmounts: {
                      ...prev.categoryAmounts,
                      [UNCLASSIFIED_CATEGORY_CODE]: e.target.value,
                    },
                  }))
                }
                fullWidth
                size="small"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blockMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => blockMutation.mutate()}
            disabled={!canSubmitBlock || blockMutation.isPending}
          >
            {blockMutation.isPending ? 'Guardando...' : editingBlock ? 'Guardar cambios' : 'Guardar ajuste'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
